import { ServiceInterface } from "@/interfaces/entities/entity_blueprints/ServiceInterface";
import { MiddlewareInterface } from "@/interfaces/entities/entity_blueprints/MiddlewareInterface";
import { EndPointInterface } from "@/interfaces/entities/entity_blueprints/EndPointInterface";
import { FunctionInterface } from "@/interfaces/entities/entity_blueprints/FunctionInterface";
import { BackendModel } from "@/data/backendViewData";

/**
 * Entity types that can be managed
 */
export enum EntityType {
  SERVICE = "service",
  MIDDLEWARE = "middleware",
  ENDPOINT = "endpoint",
  FUNCTION = "function",
  MODEL = "model",
}

/**
 * Edge types for connecting entities
 */
export enum EdgeType {
  MIDDLEWARE_TO_FUNCTION = "middleware_to_function",
  SERVICE_TO_FUNCTION = "service_to_function",
  FUNCTION_TO_SERVICE = "function_to_service",
  ENDPOINT_TO_FUNCTION = "endpoint_to_function",
  ENDPOINT_TO_MIDDLEWARE = "endpoint_to_middleware",
  MIDDLEWARE_TO_SERVICE = "middleware_to_service",
  SERVICE_TO_SERVICE = "service_to_service",
  FUNCTION_TO_FUNCTION = "function_to_function",
  MODEL_TO_ENDPOINT = "model_to_endpoint",
  MODEL_TO_SERVICE = "model_to_service",
  ENDPOINT_TO_SERVICE = "endpoint_to_service",
  ENDPOINT_TO_MODEL = "endpoint_to_model",
}

/**
 * Edge representation
 */
export interface EntityEdge {
  id: string;
  sourceId: string;
  targetId: string;
  sourceType: EntityType;
  targetType: EntityType;
  edgeType: EdgeType;
  metadata?: Record<string, unknown>;
}

/**
 * Event types for entity changes
 */
export enum EntityEventType {
  CREATED = "created",
  UPDATED = "updated",
  DELETED = "deleted",
  EDGE_ADDED = "edge_added",
  EDGE_REMOVED = "edge_removed",
}

/**
 * Entity change event
 */
export interface EntityEvent {
  type: EntityEventType;
  entityType: EntityType;
  entityId: string;
  data?: unknown;
  timestamp: Date;
}

/**
 * Event handler function type
 */
export type EntityEventHandler = (event: EntityEvent) => void | Promise<void>;

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Entity Manager
 * Provides comprehensive data management with proper abstractions for:
 * - CRUD operations
 * - Edge management (connections)
 * - Event handlers/subscriptions
 * - Validation
 * - Relationship management
 * - Getters/setters
 */
export class EntityManager {
  // Entity storage
  private services: Map<string, ServiceInterface> = new Map();
  private middlewares: Map<string, MiddlewareInterface> = new Map();
  private endpoints: Map<string, EndPointInterface> = new Map();
  private functions: Map<string, FunctionInterface> = new Map();
  private models: Map<string, BackendModel> = new Map();

  // Edge storage
  private edges: Map<string, EntityEdge> = new Map();
  private edgesBySource: Map<string, Set<string>> = new Map();
  private edgesByTarget: Map<string, Set<string>> = new Map();

  // Event handlers
  private eventHandlers: Map<EntityEventType, Set<EntityEventHandler>> = new Map();
  private globalEventHandlers: Set<EntityEventHandler> = new Set();

  // Validation rules
  private validationEnabled: boolean = true;

  /**
   * ==================== GETTERS ====================
   */

  /**
   * Get service by ID
   */
  getService(serviceId: string): ServiceInterface | undefined {
    return this.services.get(serviceId);
  }

  /**
   * Get all services
   */
  getServices(): ServiceInterface[] {
    return Array.from(this.services.values());
  }

  /**
   * Get middleware by ID
   */
  getMiddleware(middlewareId: string): MiddlewareInterface | undefined {
    return this.middlewares.get(middlewareId);
  }

  /**
   * Get all middlewares
   */
  getMiddlewares(): MiddlewareInterface[] {
    return Array.from(this.middlewares.values());
  }

  /**
   * Get endpoint by ID
   */
  getEndpoint(endpointId: string): EndPointInterface | undefined {
    return this.endpoints.get(endpointId);
  }

  /**
   * Get all endpoints
   */
  getEndpoints(): EndPointInterface[] {
    return Array.from(this.endpoints.values());
  }

  /**
   * Get function by ID
   */
  getFunction(functionId: string): FunctionInterface | undefined {
    return this.functions.get(functionId);
  }

  /**
   * Get all functions
   */
  getFunctions(): FunctionInterface[] {
    return Array.from(this.functions.values());
  }

  /**
   * Get model by ID
   */
  getModel(modelId: string): BackendModel | undefined {
    return this.models.get(modelId);
  }

  /**
   * Get all models
   */
  getModels(): BackendModel[] {
    return Array.from(this.models.values());
  }

  /**
   * Get entity by type and ID
   */
  getEntity(type: EntityType, id: string): unknown {
    switch (type) {
      case EntityType.SERVICE:
        return this.getService(id);
      case EntityType.MIDDLEWARE:
        return this.getMiddleware(id);
      case EntityType.ENDPOINT:
        return this.getEndpoint(id);
      case EntityType.FUNCTION:
        return this.getFunction(id);
      case EntityType.MODEL:
        return this.getModel(id);
      default:
        return undefined;
    }
  }

  /**
   * ==================== SETTERS ====================
   */

  /**
   * Set service (create or update)
   */
  setService(service: ServiceInterface): ValidationResult {
    const validation = this.validateService(service);
    if (!validation.valid && this.validationEnabled) {
      return validation;
    }

    const exists = this.services.has(service.id);
    this.services.set(service.id, service);

    this.emitEvent({
      type: exists ? EntityEventType.UPDATED : EntityEventType.CREATED,
      entityType: EntityType.SERVICE,
      entityId: service.id,
      data: service,
      timestamp: new Date(),
    });

    return validation;
  }

  /**
   * Set middleware (create or update)
   */
  setMiddleware(middleware: MiddlewareInterface): ValidationResult {
    const validation = this.validateMiddleware(middleware);
    if (!validation.valid && this.validationEnabled) {
      return validation;
    }

    const exists = this.middlewares.has(middleware.id);
    this.middlewares.set(middleware.id, middleware);

    this.emitEvent({
      type: exists ? EntityEventType.UPDATED : EntityEventType.CREATED,
      entityType: EntityType.MIDDLEWARE,
      entityId: middleware.id,
      data: middleware,
      timestamp: new Date(),
    });

    return validation;
  }

  /**
   * Set endpoint (create or update)
   */
  setEndpoint(endpoint: EndPointInterface): ValidationResult {
    const validation = this.validateEndpoint(endpoint);
    if (!validation.valid && this.validationEnabled) {
      return validation;
    }

    const exists = this.endpoints.has(endpoint.id);
    this.endpoints.set(endpoint.id, endpoint);

    this.emitEvent({
      type: exists ? EntityEventType.UPDATED : EntityEventType.CREATED,
      entityType: EntityType.ENDPOINT,
      entityId: endpoint.id,
      data: endpoint,
      timestamp: new Date(),
    });

    return validation;
  }

  /**
   * Set function (create or update)
   */
  setFunction(func: FunctionInterface): ValidationResult {
    const validation = this.validateFunction(func);
    if (!validation.valid && this.validationEnabled) {
      return validation;
    }

    const exists = this.functions.has(func.id);
    this.functions.set(func.id, func);

    this.emitEvent({
      type: exists ? EntityEventType.UPDATED : EntityEventType.CREATED,
      entityType: EntityType.FUNCTION,
      entityId: func.id,
      data: func,
      timestamp: new Date(),
    });

    return validation;
  }

  /**
   * Set model (create or update)
   */
  setModel(model: BackendModel): ValidationResult {
    const validation = this.validateModel(model);
    if (!validation.valid && this.validationEnabled) {
      return validation;
    }

    const exists = this.models.has(model.id);
    this.models.set(model.id, model);

    this.emitEvent({
      type: exists ? EntityEventType.UPDATED : EntityEventType.CREATED,
      entityType: EntityType.MODEL,
      entityId: model.id,
      data: model,
      timestamp: new Date(),
    });

    return validation;
  }

  /**
   * ==================== DELETION ====================
   */

  /**
   * Delete service and all its edges
   */
  deleteService(serviceId: string): boolean {
    if (!this.services.has(serviceId)) {
      return false;
    }

    // Remove all edges connected to this service
    this.removeEdgesForEntity(serviceId);

    this.services.delete(serviceId);

    this.emitEvent({
      type: EntityEventType.DELETED,
      entityType: EntityType.SERVICE,
      entityId: serviceId,
      timestamp: new Date(),
    });

    return true;
  }

  /**
   * Delete middleware and all its edges
   */
  deleteMiddleware(middlewareId: string): boolean {
    if (!this.middlewares.has(middlewareId)) {
      return false;
    }

    this.removeEdgesForEntity(middlewareId);
    this.middlewares.delete(middlewareId);

    this.emitEvent({
      type: EntityEventType.DELETED,
      entityType: EntityType.MIDDLEWARE,
      entityId: middlewareId,
      timestamp: new Date(),
    });

    return true;
  }

  /**
   * Delete endpoint and all its edges
   */
  deleteEndpoint(endpointId: string): boolean {
    if (!this.endpoints.has(endpointId)) {
      return false;
    }

    this.removeEdgesForEntity(endpointId);
    this.endpoints.delete(endpointId);

    this.emitEvent({
      type: EntityEventType.DELETED,
      entityType: EntityType.ENDPOINT,
      entityId: endpointId,
      timestamp: new Date(),
    });

    return true;
  }

  /**
   * Delete function and all its edges
   */
  deleteFunction(functionId: string): boolean {
    if (!this.functions.has(functionId)) {
      return false;
    }

    this.removeEdgesForEntity(functionId);
    this.functions.delete(functionId);

    this.emitEvent({
      type: EntityEventType.DELETED,
      entityType: EntityType.FUNCTION,
      entityId: functionId,
      timestamp: new Date(),
    });

    return true;
  }

  /**
   * Delete model and all its edges
   */
  deleteModel(modelId: string): boolean {
    if (!this.models.has(modelId)) {
      return false;
    }

    this.removeEdgesForEntity(modelId);
    this.models.delete(modelId);

    this.emitEvent({
      type: EntityEventType.DELETED,
      entityType: EntityType.MODEL,
      entityId: modelId,
      timestamp: new Date(),
    });

    return true;
  }

  /**
   * ==================== EDGE MANAGEMENT ====================
   */

  /**
   * Add an edge between two entities
   */
  addEdge(
    sourceId: string,
    targetId: string,
    sourceType: EntityType,
    targetType: EntityType,
    edgeType: EdgeType,
    metadata?: Record<string, unknown>
  ): ValidationResult {
    // Validate edge
    const sourceEntity = this.getEntity(sourceType, sourceId);
    const targetEntity = this.getEntity(targetType, targetId);

    if (!sourceEntity) {
      return {
        valid: false,
        errors: [`Source entity ${sourceId} of type ${sourceType} not found`],
        warnings: [],
      };
    }

    if (!targetEntity) {
      return {
        valid: false,
        errors: [`Target entity ${targetId} of type ${targetType} not found`],
        warnings: [],
      };
    }

    // Check if edge already exists
    const existingEdge = this.findEdge(sourceId, targetId, edgeType);
    if (existingEdge) {
      return {
        valid: false,
        errors: [`Edge already exists between ${sourceId} and ${targetId}`],
        warnings: [],
      };
    }

    const edgeId = `${sourceId}->${targetId}:${edgeType}`;
    const edge: EntityEdge = {
      id: edgeId,
      sourceId,
      targetId,
      sourceType,
      targetType,
      edgeType,
      metadata,
    };

    this.edges.set(edgeId, edge);

    // Update index maps
    if (!this.edgesBySource.has(sourceId)) {
      this.edgesBySource.set(sourceId, new Set());
    }
    this.edgesBySource.get(sourceId)!.add(edgeId);

    if (!this.edgesByTarget.has(targetId)) {
      this.edgesByTarget.set(targetId, new Set());
    }
    this.edgesByTarget.get(targetId)!.add(edgeId);

    // Update entity relationships
    this.updateEntityRelationships(edge);

    this.emitEvent({
      type: EntityEventType.EDGE_ADDED,
      entityType: sourceType,
      entityId: sourceId,
      data: edge,
      timestamp: new Date(),
    });

    return { valid: true, errors: [], warnings: [] };
  }

  /**
   * Remove an edge
   */
  removeEdge(edgeId: string): boolean {
    const edge = this.edges.get(edgeId);
    if (!edge) {
      return false;
    }

    this.edges.delete(edgeId);
    this.edgesBySource.get(edge.sourceId)?.delete(edgeId);
    this.edgesByTarget.get(edge.targetId)?.delete(edgeId);

    // Remove from entity relationships
    this.removeEntityRelationships(edge);

    this.emitEvent({
      type: EntityEventType.EDGE_REMOVED,
      entityType: edge.sourceType,
      entityId: edge.sourceId,
      data: edge,
      timestamp: new Date(),
    });

    return true;
  }

  /**
   * Find edge between two entities
   */
  findEdge(sourceId: string, targetId: string, edgeType?: EdgeType): EntityEdge | undefined {
    const sourceEdges = this.edgesBySource.get(sourceId);
    if (!sourceEdges) {
      return undefined;
    }

    for (const edgeId of sourceEdges) {
      const edge = this.edges.get(edgeId);
      if (edge && edge.targetId === targetId && (!edgeType || edge.edgeType === edgeType)) {
        return edge;
      }
    }

    return undefined;
  }

  /**
   * Get all edges for an entity
   */
  getEdgesForEntity(entityId: string): EntityEdge[] {
    const edges: EntityEdge[] = [];
    const sourceEdges = this.edgesBySource.get(entityId);
    const targetEdges = this.edgesByTarget.get(entityId);

    if (sourceEdges) {
      for (const edgeId of sourceEdges) {
        const edge = this.edges.get(edgeId);
        if (edge) edges.push(edge);
      }
    }

    if (targetEdges) {
      for (const edgeId of targetEdges) {
        const edge = this.edges.get(edgeId);
        if (edge && !sourceEdges?.has(edgeId)) edges.push(edge);
      }
    }

    return edges;
  }

  /**
   * Get all edges
   */
  getAllEdges(): EntityEdge[] {
    return Array.from(this.edges.values());
  }

  /**
   * Remove all edges for an entity
   */
  private removeEdgesForEntity(entityId: string): void {
    const edges = this.getEdgesForEntity(entityId);
    for (const edge of edges) {
      this.removeEdge(edge.id);
    }
  }

  /**
   * ==================== RELATIONSHIP HELPERS ====================
   */

  /**
   * Connect function to service
   */
  connectFunctionToService(serviceId: string, functionId: string): ValidationResult {
    return this.addEdge(
      serviceId,
      functionId,
      EntityType.SERVICE,
      EntityType.FUNCTION,
      EdgeType.SERVICE_TO_FUNCTION
    );
  }

  /**
   * Connect function to endpoint
   */
  connectFunctionToEndpoint(endpointId: string, functionId: string): ValidationResult {
    return this.addEdge(
      endpointId,
      functionId,
      EntityType.ENDPOINT,
      EntityType.FUNCTION,
      EdgeType.ENDPOINT_TO_FUNCTION
    );
  }

  /**
   * Connect middleware to endpoint
   */
  connectMiddlewareToEndpoint(endpointId: string, middlewareId: string): ValidationResult {
    return this.addEdge(
      endpointId,
      middlewareId,
      EntityType.ENDPOINT,
      EntityType.MIDDLEWARE,
      EdgeType.ENDPOINT_TO_MIDDLEWARE
    );
  }

  /**
   * Connect service to service (dependency)
   */
  connectServiceToService(sourceServiceId: string, targetServiceId: string): ValidationResult {
    return this.addEdge(
      sourceServiceId,
      targetServiceId,
      EntityType.SERVICE,
      EntityType.SERVICE,
      EdgeType.SERVICE_TO_SERVICE
    );
  }

  /**
   * Connect middleware to service
   */
  connectMiddlewareToService(middlewareId: string, serviceId: string): ValidationResult {
    return this.addEdge(
      middlewareId,
      serviceId,
      EntityType.MIDDLEWARE,
      EntityType.SERVICE,
      EdgeType.MIDDLEWARE_TO_SERVICE
    );
  }

  /**
   * Connect model to endpoint
   */
  connectModelToEndpoint(modelId: string, endpointId: string): ValidationResult {
    return this.addEdge(
      modelId,
      endpointId,
      EntityType.MODEL,
      EntityType.ENDPOINT,
      EdgeType.MODEL_TO_ENDPOINT
    );
  }

  /**
   * Disconnect entities
   */
  disconnect(sourceId: string, targetId: string, edgeType?: EdgeType): boolean {
    const edge = this.findEdge(sourceId, targetId, edgeType);
    if (edge) {
      return this.removeEdge(edge.id);
    }
    return false;
  }

  /**
   * ==================== EVENT HANDLERS ====================
   */

  /**
   * Subscribe to entity events
   */
  on(eventType: EntityEventType, handler: EntityEventHandler): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.eventHandlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Subscribe to all entity events
   */
  onAll(handler: EntityEventHandler): () => void {
    this.globalEventHandlers.add(handler);
    return () => {
      this.globalEventHandlers.delete(handler);
    };
  }

  /**
   * Emit an event
   */
  private async emitEvent(event: EntityEvent): Promise<void> {
    // Call specific event handlers
    const handlers = this.eventHandlers.get(event.type);
    if (handlers) {
      for (const handler of handlers) {
        try {
          await handler(event);
        } catch (error) {
          console.error(`Error in event handler for ${event.type}:`, error);
        }
      }
    }

    // Call global event handlers
    for (const handler of this.globalEventHandlers) {
      try {
        await handler(event);
      } catch (error) {
        console.error(`Error in global event handler:`, error);
      }
    }
  }

  /**
   * ==================== VALIDATION ====================
   */

  /**
   * Validate service
   */
  private validateService(service: ServiceInterface): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!service.id) {
      errors.push("Service ID is required");
    }
    if (!service.serviceName) {
      errors.push("Service name is required");
    }
    if (service.serviceFunctions && service.serviceFunctions.length > 0) {
      for (const func of service.serviceFunctions) {
        if (!this.functions.has(func.id)) {
          warnings.push(`Function ${func.id} referenced but not found in manager`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate middleware
   */
  private validateMiddleware(middleware: MiddlewareInterface): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!middleware.id) {
      errors.push("Middleware ID is required");
    }
    if (!middleware.middlewareName) {
      errors.push("Middleware name is required");
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate endpoint
   */
  private validateEndpoint(endpoint: EndPointInterface): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!endpoint.id) {
      errors.push("Endpoint ID is required");
    }
    if (!endpoint.path) {
      errors.push("Endpoint path is required");
    }
    if (!endpoint.method) {
      errors.push("Endpoint method is required");
    }

    if (endpoint.functionId && !this.functions.has(endpoint.functionId)) {
      warnings.push(`Function ${endpoint.functionId} referenced but not found`);
    }

    if (endpoint.middlewareIds) {
      for (const mwId of endpoint.middlewareIds) {
        if (!this.middlewares.has(mwId)) {
          warnings.push(`Middleware ${mwId} referenced but not found`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate function
   */
  private validateFunction(func: FunctionInterface): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!func.id) {
      errors.push("Function ID is required");
    }
    if (!func.functionName) {
      errors.push("Function name is required");
    }

    if (func.servicesNeeded) {
      for (const serviceId of func.servicesNeeded) {
        if (!this.services.has(serviceId)) {
          warnings.push(`Service ${serviceId} referenced but not found`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Validate model
   */
  private validateModel(model: BackendModel): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!model.id) {
      errors.push("Model ID is required");
    }
    if (!model.name) {
      errors.push("Model name is required");
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Enable/disable validation
   */
  setValidationEnabled(enabled: boolean): void {
    this.validationEnabled = enabled;
  }

  /**
   * ==================== ENTITY RELATIONSHIP UPDATES ====================
   */

  /**
   * Update entity relationships when edge is added
   */
  private updateEntityRelationships(edge: EntityEdge): void {
    switch (edge.edgeType) {
      case EdgeType.SERVICE_TO_FUNCTION: {
        const service = this.services.get(edge.sourceId);
        const func = this.functions.get(edge.targetId);
        if (service && func) {
          if (!service.serviceFunctions) {
            service.serviceFunctions = [];
          }
          if (!service.serviceFunctions.find((f) => f.id === func.id)) {
            service.serviceFunctions.push(func);
          }
          if (!service.functionIds) {
            service.functionIds = [];
          }
          if (!service.functionIds.includes(func.id)) {
            service.functionIds.push(func.id);
          }
          service.updated = true;
        }
        break;
      }
      case EdgeType.ENDPOINT_TO_FUNCTION: {
        const endpoint = this.endpoints.get(edge.sourceId);
        const func = this.functions.get(edge.targetId);
        if (endpoint && func) {
          (endpoint as any).handlerFunction = func;
          endpoint.functionId = func.id;
        }
        break;
      }
      case EdgeType.ENDPOINT_TO_MIDDLEWARE: {
        const endpoint = this.endpoints.get(edge.sourceId);
        const middleware = this.middlewares.get(edge.targetId);
        if (endpoint && middleware) {
          if (!endpoint.middlewareIds) {
            endpoint.middlewareIds = [];
            (endpoint as any).middlewareNames = [];
          }
          if (!endpoint.middlewareIds.includes(middleware.id)) {
            endpoint.middlewareIds.push(middleware.id);
            (endpoint as any).middlewareNames?.push(middleware.middlewareName);
          }
        }
        break;
      }
      case EdgeType.SERVICE_TO_SERVICE: {
        const sourceService = this.services.get(edge.sourceId);
        if (sourceService) {
          if (!sourceService.servicesRequired) {
            sourceService.servicesRequired = [];
          }
          if (!sourceService.servicesRequired.includes(edge.targetId)) {
            sourceService.servicesRequired.push(edge.targetId);
          }
        }
        break;
      }
      case EdgeType.MIDDLEWARE_TO_SERVICE: {
        const middleware = this.middlewares.get(edge.sourceId);
        if (middleware) {
          if (!middleware.servicesRequired) {
            middleware.servicesRequired = [];
          }
          if (!middleware.servicesRequired.includes(edge.targetId)) {
            middleware.servicesRequired.push(edge.targetId);
          }
        }
        break;
      }
      case EdgeType.MODEL_TO_ENDPOINT: {
        const endpoint = this.endpoints.get(edge.targetId);
        if (endpoint) {
          endpoint.modelName = this.models.get(edge.sourceId)?.name;
          if (!endpoint.modelsRequired) {
            endpoint.modelsRequired = [];
          }
          if (!endpoint.modelsRequired.includes(edge.sourceId)) {
            endpoint.modelsRequired.push(edge.sourceId);
          }
        }
        break;
      }
    }
  }

  /**
   * Remove entity relationships when edge is removed
   */
  private removeEntityRelationships(edge: EntityEdge): void {
    switch (edge.edgeType) {
      case EdgeType.SERVICE_TO_FUNCTION: {
        const service = this.services.get(edge.sourceId);
        if (service) {
          if (service.serviceFunctions) {
            service.serviceFunctions = service.serviceFunctions.filter((f) => f.id !== edge.targetId);
          }
          if (service.functionIds) {
            service.functionIds = service.functionIds.filter((id) => id !== edge.targetId);
          }
          service.updated = true;
        }
        break;
      }
      case EdgeType.ENDPOINT_TO_FUNCTION: {
        const endpoint = this.endpoints.get(edge.sourceId);
        if (endpoint) {
          (endpoint as any).handlerFunction = undefined;
          endpoint.functionId = undefined;
          endpoint.updated = true;
        }
        break;
      }
      case EdgeType.ENDPOINT_TO_MIDDLEWARE: {
        const endpoint = this.endpoints.get(edge.sourceId);
        if (endpoint && endpoint.middlewareIds) {
          endpoint.middlewareIds = endpoint.middlewareIds.filter((id) => id !== edge.targetId);
          if ((endpoint as any).middlewareNames) {
            const middleware = this.middlewares.get(edge.targetId);
            (endpoint as any).middlewareNames = (endpoint as any).middlewareNames.filter(
              (name: string) => name !== middleware?.middlewareName
            );
          }
          endpoint.updated = true;
        }
        break;
      }
      case EdgeType.SERVICE_TO_SERVICE: {
        const sourceService = this.services.get(edge.sourceId);
        if (sourceService && sourceService.servicesRequired) {
          sourceService.servicesRequired = sourceService.servicesRequired.filter(
            (id) => id !== edge.targetId
          );
        }
        break;
      }
      case EdgeType.MIDDLEWARE_TO_SERVICE: {
        const middleware = this.middlewares.get(edge.sourceId);
        if (middleware && middleware.servicesRequired) {
          middleware.servicesRequired = middleware.servicesRequired.filter((id) => id !== edge.targetId);
        }
        break;
      }
      case EdgeType.MODEL_TO_ENDPOINT: {
        const endpoint = this.endpoints.get(edge.targetId);
        if (endpoint) {
          if (endpoint.modelName === this.models.get(edge.sourceId)?.name) {
            endpoint.modelName = undefined;
          }
          if (endpoint.modelsRequired) {
            endpoint.modelsRequired = endpoint.modelsRequired.filter((id) => id !== edge.sourceId);
          }
        }
        break;
      }
    }
  }

  /**
   * ==================== UTILITY ====================
   */

  /**
   * Clear all entities and edges
   */
  clear(): void {
    this.services.clear();
    this.middlewares.clear();
    this.endpoints.clear();
    this.functions.clear();
    this.models.clear();
    this.edges.clear();
    this.edgesBySource.clear();
    this.edgesByTarget.clear();
  }

  /**
   * Get statistics
   */
  getStats(): {
    services: number;
    middlewares: number;
    endpoints: number;
    functions: number;
    models: number;
    edges: number;
  } {
    return {
      services: this.services.size,
      middlewares: this.middlewares.size,
      endpoints: this.endpoints.size,
      functions: this.functions.size,
      models: this.models.size,
      edges: this.edges.size,
    };
  }
}

